import {
  UilTrash,
  UilPen,
  UilPlus,
  UilImport,
  UilUpload,
  UilQrcodeScan,
  UilCopy,
} from '@iconscout/react-unicons';
import {
  Button,
  ButtonGroup,
  IconButton,
  Divider,
  ConfirmDialog,
  Notification,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tooltip,
  TextField,
  Typography,
} from '@xbotvn/mui';
import html2canvas from 'html2canvas';
import {
  find, get, values, map, replace, toLower,
} from 'lodash';
import React, {
  useMemo, useState,
} from 'react';
import QRCode from 'react-qr-code';
import {
  useDispatch, useSelector,
} from 'react-redux';

import { childrenBookApi } from '../../../api';
import ApprovalStatusChip from '../../../components/ApprovalStatusChip';
import DropzoneModal from '../../../components/DropzoneModal';
import Table from '../../../components/Table';
import Waiting from '../../../components/Waiting';
import { CHILDREN_BOOK } from '../../../libs/config';
import { SUBJECTS_BOOK, APPROVAL_OPTIONS, normalizeText } from '../../../libs/utils';
import {
  CHILDRENBOOK,
  FILE,
} from '../../../redux/actions/constants';
import { importExcel } from '../Helpers';
import * as Styles from '../styles';

import Editor from './Editor';

export default function ChildrenBook() {
  const dispatch = useDispatch();

  const {
    handling, childrenBooks, activeUnit, unitInfo,
  } = useSelector(({
    user, childrenBook, unit,
  }) => ({
    handling: get(childrenBook, 'handling', false),
    activeUnit: user.activeUnit,
    childrenBooks: childrenBook.data,
    unitInfo: unit.data?.[user?.activeUnit],
  }));

  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editor, setEditor] = useState();
  const [qrDialog, setQrDialog] = useState();
  const [sendConfirm, setSendConfirm] = useState();

  const hasParent = !!unitInfo?.parentId;

  const convertQRtoPNG = async (callback) => {
    const elementQR = document.querySelector('#qr-code-container');

    const canvas = await html2canvas(elementQR);

    canvas.toBlob((blob) => {
      callback(blob);
    }, 'image/png');
  };

  const handleSendBook = async (bookId) => {
    try {
      const subjectBook = SUBJECTS_BOOK[childrenBooks?.[bookId]?.subject];
      const {
        data,
      } = await childrenBookApi.submit({
        _id: bookId,
        unitId: activeUnit,
        data: {
          tags: ['Sách cho trẻ', subjectBook],
        },
      });

      if (data?.record?._id) {
        dispatch({
          type: CHILDRENBOOK.update,
          data: {
            [data.record._id]: data.record,
          },
          merge: true,
        });
      }
      Notification.success('Đã gửi sách để xét duyệt');
    } catch (error) {
      Notification.error('Gửi sách thất bại');
    } finally {
      setSendConfirm();
    }
  };

  const renderWithTooltip = (value) => (
    <Tooltip title={value || ''} placement="top">
      <div style={{
        width: 'fit-content',
        maxWidth: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      >
        {value || ''}
      </div>
    </Tooltip>
  );

  const columns = [
    {
      Header: 'STT',
      id: 'stt',
      disableFilters: true,
      style: {
        width: 200,
        minWidth: 50,
      },
      accessor: (e, index) => index + 1,
    },
    {
      Header: 'Nhan đề',
      accessor: 'name',
      style: {
        width: 500,
        minWidth: 200,
      },
      Cell: (rowProps) => {
        const { value } = rowProps;
        return renderWithTooltip(value);
      },
    },
    {
      Header: 'Chủ đề',
      style: {
        width: 500,
        minWidth: 200,
      },
      accessor: ({ subject }) => SUBJECTS_BOOK?.[subject] || '',
    },
    {
      Header: 'Đường link ảnh bìa',
      style: {
        width: 500,
        minWidth: 250,
      },
      disableFilters: true,
      accessor: 'coverLink',
      Cell: (rowProps) => {
        const { value } = rowProps;
        return renderWithTooltip(value);
      },
    },
    {
      Header: 'Đường link sách online',
      style: {
        width: 500,
        minWidth: 250,
      },
      disableFilters: true,
      accessor: 'bookLink',
      Cell: (rowProps) => {
        const { value } = rowProps;
        return renderWithTooltip(value);
      },
    },
    ...(hasParent ? [
      {
        Header: 'Trạng thái',
        accessor: 'approvalStatus',
        /* eslint-disable react/prop-types */
        Filter: ({
          column: {
            filterValue,
            setFilter,
          },
        }) => (
          <TextField
            select
            SelectProps={{
              native: true,
            }}
            value={filterValue || ''}
            onChange={(event) => setFilter(event.target.value || undefined)}
          >
            <option value="">Tất cả</option>
            {APPROVAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
        ),
        Cell: (rowProps) => {
          const {
            value,
            row,
          } = rowProps;
          const approvalNote = row?.original?.approvalNote;
          return (
            <Box display="flex" justifyContent="center">
              <ApprovalStatusChip status={value} note={approvalNote} />
            </Box>
          );
        },
        style: {
          width: 150,
          minWidth: 120,
          textAlign: 'center',
          verticalAlign: 'middle',

        },
      },
      {
        Header: 'Gửi duyệt',
        id: 'send',
        disableFilters: true,
        style: {
          width: 100,
          textAlign: 'center',
          verticalAlign: 'middle',
        },
        Cell: (rowProps) => {
          const rowValue = get(rowProps, 'row.original', {});
          const status = rowValue?.approvalStatus || 'draft';
          const isSend = status === 'draft' || status === 'rejected';

          if (isSend) {
            return (
              <Box display="flex" justifyContent="center">
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  startIcon={<UilUpload />}
                  onClick={() => setSendConfirm(rowValue)}
                >
                  Gửi
                </Button>
              </Box>
            );
          }
          return null;
        },
      },
    ] : []),
    {
      Header: () => (
        <Button
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => setEditor({
            name: '',
            subject: '',
            coverLink: '',
            bookLink: '',
            unitId: activeUnit,
          })}
          startIcon={<UilPlus />}
        >
          Thêm
        </Button>
      ),
      id: 'add',
      disableFilters: true,
      Cell: (rowProps) => {
        const rowValue = get(rowProps, 'row.original', {});
        return (
          <ButtonGroup>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
            }}
            >
              <IconButton
                size="small"
                color="primary"
                onClick={() => setQrDialog(rowValue?.bookLink)}
                title="Xem QR code"
              >
                <UilQrcodeScan />
              </IconButton>
            </Box>
            <IconButton
              onClick={() => setEditor(rowValue)}
              icon="edit"
              size="small"
              color="primary"
              sx={{
                marginRight: 0.5,
              }}
            >
              <UilPen />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            <IconButton
              color="error"
              size="small"
              variant="outlined"
              onClick={() => setRemoveConfirm(rowValue._id)}
              sx={{
                marginLeft: 0.5,
              }}
            >
              <UilTrash />
            </IconButton>

          </ButtonGroup>
        );
      },
    },
  ];

  const editModal = useMemo(() => (
    <Editor
      onClose={() => setEditor()}
      onHandler={(params) => {
        dispatch({
          type: CHILDRENBOOK.handlers.update,
          data: params,
          onSuccess: () => {
            setEditor();
          },
        });
      }}
      data={editor}
      unitInfo={unitInfo}
    />
  ), [editor]);

  const renderModalConfirm = useMemo(() => (
    <ConfirmDialog
      title="Bạn có chắc chắn muốn xóa?"
      color="error"
      confirmLabel="Xoá"
      cancelLabel="Huỷ"
      maxWidth="sm"
      fullWidth
      open
      onClose={() => setRemoveConfirm()}
      onConfirm={() => {
        dispatch({
          type: CHILDRENBOOK.handlers.remove,
          _id: removeConfirm,
          onSuccess: () => {
            setRemoveConfirm();
          },
        });
      }}
    />
  ));

  const renderModalQRCode = () => (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      onClose={() => setQrDialog()}
    >
      <DialogTitle
        onClose={() => setQrDialog()}
      >
        QR Code
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            padding: 2,
          }}
        >
          <Box id="qr-code-container">
            <QRCode value={qrDialog} size={256} />
          </Box>
          <Box
            sx={{
              wordBreak: 'break-all',
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: '0.875rem',
              marginBottom: 1,
            }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<UilCopy />}
            onClick={async () => {
              convertQRtoPNG(async (blob) => {
                try {
                  await navigator.clipboard.write([
                    new ClipboardItem({ 'image/png': blob }),
                  ]);
                  Notification.success('Đã sao chép QR code');
                } catch (err) {
                  console.log(err);
                  Notification.error('Sao chép QR code thất bại');
                }
              });
            }}
          >
            Sao chép ảnh
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  const normalizeStr = replace(normalizeText(toLower(unitInfo?.name || '')), / /g, '_');
  const publicLink = `${window.location.origin}/sachChoTre/${normalizeStr}/${activeUnit}`;

  return (
    <>
      {removeConfirm && renderModalConfirm}
      {handling ? <Waiting /> : null}
      {editor ? editModal : null}
      {qrDialog ? renderModalQRCode() : null}
      {showImport && (
        <DropzoneModal
          onConfirm={(files) => {
            if (files.length === 0) return;
            importExcel({
              files,
              activeUnit,
              config: CHILDREN_BOOK,
              onSuccess: (records) => {
                if (records.some(({ errors }) => errors)) {
                  const errorStr = find((records)?.errors, (rec) => rec.errors) || '';
                  Notification.error(errorStr);
                } else {
                  dispatch({
                    type: CHILDRENBOOK.handlers.import,
                    data: records,
                  });
                  setShowImport();
                }
              },
            });
          }}
          onClose={() => setShowImport()}
        />
      )}
      {sendConfirm ? (
        <ConfirmDialog
          title="Gửi sách để xét duyệt?"
          confirmLabel="Gửi"
          cancelLabel="Huỷ"
          maxWidth="sm"
          fullWidth
          open
          onClose={() => setSendConfirm()}
          onConfirm={() => handleSendBook(sendConfirm?._id)}
        />
      ) : null}
      <Styles.ButtonGroup>
        <Button
          startIcon={<UilUpload />}
          color="success"
          size="small"
          onClick={() => setShowImport(true)}
          sx={{
            marginRight: 1,
          }}
        >
          Nhập từ Excel
        </Button>
        <Button
          startIcon={<UilImport />}
          color="primary"
          size="small"
          onClick={() => dispatch({
            type: FILE.handlers.download,
            data: {
              unitId: activeUnit,
              fileName: 'Mau_SachChoTre.xlsx',
            },
          })}
        >
          Tải file mẫu
        </Button>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto',
        }}
        >
          <Tooltip title={publicLink} placement="top">
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => window.open(publicLink, '_blank')}
            >
              Link công khai
            </Button>
          </Tooltip>
          <Tooltip title="Sao chép link công khai" placement="top">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                navigator.clipboard.writeText(publicLink);
                Notification.success('Đã sao chép link');
              }}
            >
              <UilCopy />
            </IconButton>
          </Tooltip>
        </Box>
      </Styles.ButtonGroup>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 1 }}
      >
        *Đưa con trỏ chuột vào trạng thái để xem nhận xét từ cấp quản lý nếu có
      </Typography>
      <Table
        columns={columns}
        data={map(values(childrenBooks), (item) => ({
          ...item,
          approvalStatus: item.approvalStatus || 'draft',
        }))}
        disableGlobalFilter
        alwaysShowPagination
      />
    </>
  );
}
